// usage-media-view.tsx — Media (LMS/VMS transcode) daily usage chart for the usage tab.
// Shows lms_transcode + vms_transcode minutes per day from the media analytics store.
import { useMemo } from 'react';
import { Button, Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState, Stat, StatGroup } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { useOpsMediaAnalytics } from '@/api/operations';

/* ── Chart palette — Muted Material 400 ──────────────────────────────────── */

const COLOR_LMS = CHART_COLORS[3]; // purple — closest to indigo
const COLOR_VMS = CHART_COLORS[1]; // orange

/* ── Props ────────────────────────────────────────────────────────────────── */

interface MediaUsageViewProps {
  serviceId: string;
  /** YYYY-MM-DD local date strings forwarded as gte/lte query params. */
  fromDate: string;
  toDate: string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * MediaUsageView shows daily transcode minutes for LMS and VMS.
 * Both queries are unconditional (React hooks rules); the BE returns an empty
 * series (not an error) when the service has no media zones for that metric.
 */
export function MediaUsageView({ serviceId, fromDate, toDate }: MediaUsageViewProps) {
  const lms = useOpsMediaAnalytics(serviceId, { metric: 'lms_transcode', gte: fromDate, lte: toDate });
  const vms = useOpsMediaAnalytics(serviceId, { metric: 'vms_transcode', gte: fromDate, lte: toDate });

  // Merge both series by ts into one chart dataset.
  const chartData = useMemo(() => {
    const map = new Map<string, { t: string; lms: number; vms: number }>();
    for (const p of lms.data?.series ?? []) {
      map.set(p.ts, { t: p.ts, lms: p.value, vms: 0 });
    }
    for (const p of vms.data?.series ?? []) {
      const row = map.get(p.ts);
      if (row) { row.vms = p.value; } else { map.set(p.ts, { t: p.ts, lms: 0, vms: p.value }); }
    }
    return Array.from(map.values()).sort((a, b) => a.t.localeCompare(b.t));
  }, [lms.data, vms.data]);

  const totalLms = useMemo(
    () => (lms.data?.series ?? []).reduce((acc, p) => acc + p.value, 0),
    [lms.data],
  );
  const totalVms = useMemo(
    () => (vms.data?.series ?? []).reduce((acc, p) => acc + p.value, 0),
    [vms.data],
  );

  const isLoading = lms.isLoading || vms.isLoading;
  const isError = lms.isError || vms.isError;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {isError && (
        <Alert
          tone="danger"
          title="Failed to load media usage data"
          actions={
            <Button size="sm" variant="ghost" onClick={() => { lms.refetch(); vms.refetch(); }}>
              Retry
            </Button>
          }
        />
      )}

      {!isError && (
        <StatGroup columns={2}>
          {isLoading ? (
            <><Skeleton style={{ height: 72 }} /><Skeleton style={{ height: 72 }} /></>
          ) : (
            <>
              <Stat label="LMS Transcode (total)" value={`${totalLms.toFixed(0)} min`} hint={`${fromDate} → ${toDate}`} />
              <Stat label="VMS Transcode (total)" value={`${totalVms.toFixed(0)} min`} />
            </>
          )}
        </StatGroup>
      )}

      {!isError && (
        <Card>
          <CardBody>
            <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>
              Transcode minutes per day
            </CardTitle>
            {isLoading ? (
              <Skeleton style={{ height: 240 }} />
            ) : chartData.length > 0 ? (
              <Chart
                type="bar"
                height={240}
                data={chartData}
                xKey="t"
                series={[
                  { key: 'lms', label: 'LMS', color: COLOR_LMS },
                  { key: 'vms', label: 'VMS', color: COLOR_VMS },
                ]}
                showLegend
                showGrid
                valueFormatter={(v) => `${(typeof v === 'number' ? v : Number(v) || 0).toFixed(0)} min`}
              />
            ) : (
              <EmptyState size="sm" title="No media data" description="No transcode data found for the selected range." />
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
